import { For, Show, createSignal, type Component } from 'solid-js';

import { allocatePassive, respecPassives } from '../net/protocol';
import type { GameStore } from '../state/store';
import { slotSigil } from './actionbar';
import { Icon } from './Icon';
import { FloatingTooltip } from './ItemTooltip';
import {
  blockedText,
  effectLines,
  gateSummary,
  passiveCategories,
  passiveRows,
  ranksSpent,
  type PassiveRow,
} from './passives';

/**
 * The generic passive pool: five categories of flat, punchy nodes.
 *
 * A panel of its own rather than a section of the skills panel, for the reason the
 * skills panel is not a section of the character sheet — they are different
 * questions. "What can I cast" and "what am I permanently better at" are chosen at
 * different moments and from different currencies.
 *
 * **There is no tree drawn here, and there is not meant to be.** The pool has no
 * connectivity, no prerequisite node and no position: any node whose level and
 * attribute requirements the character meets can take a point. That is what makes
 * this a list, and it is the whole reason the constellation design was replaced —
 * forty universal nodes are balanceable and a five-hundred-node graph is not.
 *
 * **Every node is listed, including the unreachable ones**, which is the lesson the
 * skills panel taught: a fresh character opening a panel to an empty list never
 * learns the system exists. Locked rows are greyed and say what they are waiting
 * for, and stay fully hoverable so what they grant can be read.
 */
export const PassivesPanel: Component<{ store: GameStore }> = (props) => {
  const sheet = () => props.store.character();

  const rows = () =>
    passiveRows(
      props.store.passiveCatalogue(),
      sheet()?.passives ?? [],
      sheet()?.attributes.total ?? null,
    );

  const points = () => sheet()?.progression.passivePoints ?? 0;
  const spent = () => ranksSpent(rows());

  return (
    <Show when={props.store.menu() === 'passives'}>
      <div
        class="sheet hud-interactive"
        data-testid="passives-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Passives"
      >
        <div class="plate plate--riveted sheet__panel">
          <header class="sheet__head">
            <h2 class="sheet__title">Passives</h2>
            <button
              type="button"
              class="sheet__close"
              data-testid="passives-close"
              onClick={() => props.store.closeMenu()}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <Show
            when={sheet()}
            fallback={
              <p class="sheet__waiting" data-testid="passives-waiting">
                Awaiting the server's reckoning of you.
              </p>
            }
          >
            <section class="sheet__skills sheet__skills--panel" aria-label="The passive pool">
              <p class="sheet__points">
                <Show
                  when={points() > 0}
                  fallback={
                    <span class="sheet__seam">
                      No passive points. Levelling grants one each.
                    </span>
                  }
                >
                  <strong data-testid="passive-points">{points()}</strong> passive point
                  {points() === 1 ? '' : 's'} to spend
                </Show>
              </p>

              <p class="skills__summary" data-testid="passives-summary">
                {spent()} rank{spent() === 1 ? '' : 's'} spent · {rows().length} nodes in all
              </p>

              {/*
                Refunding everything at once, matching the server. It is free, which is
                a chosen default rather than a spec value: there is no currency to
                charge in until the town economy lands.
              */}
              <button
                type="button"
                class="skills__button"
                data-testid="passives-respec"
                aria-disabled={spent() === 0}
                title={
                  spent() > 0
                    ? 'Refund every allocated passive point'
                    : 'Nothing has been allocated'
                }
                onClick={() => {
                  if (spent() > 0) props.store.send(respecPassives());
                }}
              >
                Refund all
              </button>

              <For each={passiveCategories(props.store.passiveCatalogue())}>
                {(category) => (
                  <div class="passives__group" data-testid={`passives-group-${category}`}>
                    <h3 class="passives__heading">{category}</h3>
                    <ul class="skills">
                      <For each={rows().filter((r) => r.def.category === category)}>
                        {(row) => <PassiveEntry store={props.store} row={row} />}
                      </For>
                    </ul>
                  </div>
                )}
              </For>
            </section>
          </Show>
        </div>
      </div>
    </Show>
  );
};

/**
 * One node.
 *
 * The rank pips are the whole readout: how many points are in, out of how many will
 * fit. A row that cannot take another point still renders its button — but with
 * `aria-disabled` rather than `disabled`, because a disabled button fires no pointer
 * events at all and therefore cannot be hovered, which would make the nodes you are
 * *levelling towards* the only ones whose effects you could not read.
 */
const PassiveEntry: Component<{ store: GameStore; row: PassiveRow }> = (props) => {
  let anchorEl: HTMLLIElement | undefined;
  const [hovered, setHovered] = createSignal(false);
  const def = () => props.row.def;

  return (
    <li
      class="skills__row passives__row"
      ref={anchorEl}
      classList={{
        'skills__row--learned': props.row.ranks > 0,
        'skills__row--locked': !props.row.available && props.row.ranks === 0,
      }}
      data-testid={`passive-${def().passiveId}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div class="skills__head">
        {/* The same column the skills panel runs down its own rows — both are lists to
            scan, and the two panels sit behind adjacent keys. */}
        <Icon class="skills__icon" iconId={def().iconId} glyph={slotSigil(def().name)} />
        <span class="skills__name">{def().name}</span>
        <span class="passives__rank" data-testid={`passive-rank-${def().passiveId}`}>
          {props.row.ranks}/{def().maxRank}
        </span>
        <span class="skills__gate">{gateSummary(def())}</span>
      </div>

      <p class="skills__desc">{def().description}</p>

      <ul class="passives__effects" data-testid={`passive-effects-${def().passiveId}`}>
        <For each={effectLines(def())}>
          {(line) => <li class="passives__effect">{line} per rank</li>}
        </For>
      </ul>

      <div class="skills__actions">
        <button
          type="button"
          class="skills__button"
          data-testid={`allocate-${def().passiveId}`}
          aria-disabled={!props.row.available}
          title={props.row.available ? `Put a point into ${def().name}` : blockedText(props.row)}
          onClick={() => {
            if (props.row.available) props.store.send(allocatePassive(def().passiveId));
          }}
        >
          Rank up
        </button>

        <Show when={!props.row.available && blockedText(props.row) !== ''}>
          <span class="skills__locked" data-testid={`blocked-${def().passiveId}`}>
            {blockedText(props.row)}
          </span>
        </Show>
      </div>

      <FloatingTooltip
        anchor={anchorEl}
        when={hovered()}
        class="skills__tip"
        testId={`passive-tip-${def().passiveId}`}
      >
        <p class="skills__tip-name">{def().name}</p>
        <p class="skills__tip-meta">
          {def().category} · rank {props.row.ranks} of {def().maxRank}
        </p>
        <p class="skills__tip-desc">{def().description}</p>
        <For each={effectLines(def())}>
          {(line) => <p class="skills__tip-cost">{line} per rank</p>}
        </For>
        <p class="skills__tip-gate" classList={{ 'skills__tip-gate--unmet': !props.row.available }}>
          Requires {gateSummary(def())}
        </p>
      </FloatingTooltip>
    </li>
  );
};
